import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format, parseISO, isWithinInterval, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Plus, DollarSign, Calendar, Edit2, Trash2, Filter, Search, FileText, Lock } from 'lucide-react';

const EXPENSE_CATEGORIES = [
  { value: 'feed', label: 'Feed' },
  { value: 'farrier', label: 'Farrier' },
  { value: 'vet', label: 'Veterinary' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'entry_fee', label: 'Entry Fee' },
  { value: 'transport', label: 'Transport' },
  { value: 'lesson', label: 'Lesson' },
  { value: 'boarding', label: 'Boarding' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' }
];

const DATE_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

// ExpenseForm defined OUTSIDE to prevent re-render issues
const ExpenseForm = ({ data, onChange, onSubmit, horses, isEdit = false }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Category *</Label>
      <Select value={data.category || ''} onValueChange={(v) => onChange({ ...data, category: v })}>
        <SelectTrigger data-testid="expense-category-select">
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent>
          {EXPENSE_CATEGORIES.map(cat => (
            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Horse</Label>
      <Select value={data.horse_id || 'general'} onValueChange={(v) => onChange({ ...data, horse_id: v === 'general' ? null : v })}>
        <SelectTrigger data-testid="expense-horse-select">
          <SelectValue placeholder="Select horse" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="general">General (No horse)</SelectItem>
          {horses.map(horse => (
            <SelectItem key={horse.horse_id} value={horse.horse_id}>{horse.stable_name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Amount *</Label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            type="number" 
            step="0.01"
            min="0"
            value={data.amount || ''} 
            onChange={(e) => onChange({ ...data, amount: e.target.value })}
            className="pl-10"
            required
            data-testid="expense-amount-input"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Date *</Label>
        <Input 
          type="date" 
          value={data.date || ''} 
          onChange={(e) => onChange({ ...data, date: e.target.value })}
          required
          data-testid="expense-date-input"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label>Vendor / Description</Label>
      <Input 
        value={data.vendor || ''} 
        onChange={(e) => onChange({ ...data, vendor: e.target.value })}
        placeholder="e.g., Dover Saddlery, Local Vet Clinic"
        data-testid="expense-vendor-input"
      />
    </div>

    <div className="space-y-2">
      <Label>Notes</Label>
      <Textarea 
        value={data.notes || ''} 
        onChange={(e) => onChange({ ...data, notes: e.target.value })}
        placeholder="Additional details..."
        data-testid="expense-notes-input"
      />
    </div>

    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="expense-submit-btn">
      {isEdit ? 'Update Expense' : 'Add Expense'}
    </Button>
  </form>
);

export const ExpensesPage = () => {
  const { token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const [expenses, setExpenses] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Filters
  const [filterHorse, setFilterHorse] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const emptyExpense = {
    horse_id: null,
    category: '',
    amount: '',
    vendor: '',
    date: '',
    notes: ''
  };

  const [newExpense, setNewExpense] = useState(emptyExpense);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [expensesRes, horsesRes] = await Promise.all([
        fetch(`${API}/expenses`, { headers }),
        fetch(`${API}/horses`, { headers })
      ]);

      if (expensesRes.ok) setExpenses(await expensesRes.json());
      if (horsesRes.ok) setHorses(await horsesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!newExpense.category || !newExpense.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API}/expenses`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          ...newExpense,
          amount: parseFloat(newExpense.amount)
        })
      });

      if (response.ok) {
        const expense = await response.json();
        setExpenses([expense, ...expenses]);
        setDialogOpen(false);
        setNewExpense(emptyExpense);
        toast.success('Expense added!');
      }
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleEditExpense = async (e) => {
    e.preventDefault();
    if (!editingExpense) return;

    try {
      const response = await fetch(`${API}/expenses/${editingExpense.expense_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          ...editingExpense,
          amount: parseFloat(editingExpense.amount)
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setExpenses(expenses.map(e => e.expense_id === updated.expense_id ? updated : e));
        setEditDialogOpen(false);
        setEditingExpense(null);
        toast.success('Expense updated!');
      }
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      const response = await fetch(`${API}/expenses/${expenseId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setExpenses(expenses.filter(e => e.expense_id !== expenseId));
        toast.success('Expense deleted');
      }
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const getHorseName = (horseId) => {
    if (!horseId) return 'General';
    const horse = horses.find(h => h.horse_id === horseId);
    return horse?.stable_name || 'Unknown';
  };

  const getCategoryLabel = (category) => {
    const cat = EXPENSE_CATEGORIES.find(c => c.value === category);
    return cat?.label || category?.replace('_', ' ');
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    // Horse filter
    if (filterHorse === 'general' && expense.horse_id) return false;
    if (filterHorse === 'horses_only' && !expense.horse_id) return false;
    if (filterHorse !== 'all' && filterHorse !== 'general' && filterHorse !== 'horses_only' && expense.horse_id !== filterHorse) return false;

    // Date range filter
    if (filterDateRange !== 'all' && expense.date) {
      const expenseDate = parseISO(expense.date);
      const now = new Date();
      
      if (filterDateRange === 'this_month') {
        if (!isWithinInterval(expenseDate, { start: startOfMonth(now), end: endOfMonth(now) })) return false;
      } else if (filterDateRange === 'last_month') {
        const lastMonth = subMonths(now, 1);
        if (!isWithinInterval(expenseDate, { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) })) return false;
      } else if (filterDateRange === 'last_3_months') {
        if (!isWithinInterval(expenseDate, { start: subMonths(now, 3), end: now })) return false;
      } else if (filterDateRange === 'last_6_months') {
        if (!isWithinInterval(expenseDate, { start: subMonths(now, 6), end: now })) return false;
      } else if (filterDateRange === 'this_year') {
        if (expenseDate.getFullYear() !== now.getFullYear()) return false;
      } else if (filterDateRange === 'custom' && customStartDate && customEndDate) {
        if (!isWithinInterval(expenseDate, { start: parseISO(customStartDate), end: parseISO(customEndDate) })) return false;
      }
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const horseName = getHorseName(expense.horse_id).toLowerCase();
      const category = getCategoryLabel(expense.category).toLowerCase();
      const vendor = (expense.vendor || '').toLowerCase();
      if (!horseName.includes(term) && !category.includes(term) && !vendor.includes(term)) return false;
    }

    return true;
  });

  // Sort by date (newest first)
  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(b.date) - new Date(a.date);
  });

  // Calculate totals
  const totalFiltered = sortedExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalAll = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-10 w-32 skeleton rounded-lg" />
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="expenses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Finance Vault</h1>
            <p className="text-muted-foreground mt-1">
              {expenses.length} expenses • ${totalAll.toFixed(2)} total
            </p>
          </div>
          
          <div className="flex gap-2">
            <Link to="/reports?type=expense">
              <Button variant="outline" className="gap-2" data-testid="generate-expense-report-btn">
                <FileText className="w-4 h-4" />
                Generate Report
              </Button>
            </Link>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-forest hover:bg-forest-dark gap-2 disabled:opacity-50" 
                  data-testid="add-expense-btn"
                  onClick={(e) => {
                    if (!canAddEdit()) {
                      e.preventDefault();
                      requireSubscription();
                    }
                  }}
                  disabled={!canAddEdit()}
                >
                  {!canAddEdit() && <Lock className="w-4 h-4" />}
                  <Plus className="w-4 h-4" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif">Add Expense</DialogTitle>
                </DialogHeader>
                <ExpenseForm
                  data={newExpense}
                  onChange={setNewExpense}
                  onSubmit={handleAddExpense}
                  horses={horses.filter(h => h.status === 'current' || h.horse_tab !== 'former')}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-card bg-white">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2 min-w-[150px]">
                <Label className="flex items-center gap-2">
                  <Filter className="w-4 h-4" /> Horse
                </Label>
                <Select value={filterHorse} onValueChange={setFilterHorse}>
                  <SelectTrigger data-testid="filter-horse-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (Horses + General)</SelectItem>
                    <SelectItem value="horses_only">All Horses</SelectItem>
                    <SelectItem value="general">General Only</SelectItem>
                    {horses.filter(h => h.status === 'current' || h.horse_tab !== 'former').map(horse => (
                      <SelectItem key={horse.horse_id} value={horse.horse_id}>{horse.stable_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 min-w-[150px]">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Date Range
                </Label>
                <Select value={filterDateRange} onValueChange={setFilterDateRange}>
                  <SelectTrigger data-testid="filter-date-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {filterDateRange === 'custom' && (
                <>
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input 
                      type="date" 
                      value={customStartDate} 
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      data-testid="custom-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input 
                      type="date" 
                      value={customEndDate} 
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      data-testid="custom-end-date"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2 flex-1 min-w-[200px]">
                <Label className="flex items-center gap-2">
                  <Search className="w-4 h-4" /> Search
                </Label>
                <Input 
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-expenses-input"
                />
              </div>
            </div>

            {(filterHorse !== 'all' || filterDateRange !== 'all' || searchTerm) && (
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {sortedExpenses.length} of {expenses.length} expenses
                </p>
                <p className="font-semibold text-lg text-saddle">
                  Filtered Total: ${totalFiltered.toFixed(2)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expenses List */}
        {expenses.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-16 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <h3 className="text-lg font-semibold mb-2">No Expenses Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Track your horse-related expenses to manage your budget.
              </p>
            </CardContent>
          </Card>
        ) : sortedExpenses.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-16 text-center">
              <Filter className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <h3 className="text-lg font-semibold mb-2">No Matching Expenses</h3>
              <p className="text-muted-foreground">
                Try adjusting your filters to see more results.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedExpenses.map((expense, i) => (
              <Card 
                key={expense.expense_id} 
                className="border-0 shadow-card bg-white animate-slideUp"
                style={{ animationDelay: `${i * 0.03}s` }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-saddle/10">
                      <DollarSign className="w-5 h-5 text-saddle" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold capitalize">{getCategoryLabel(expense.category)}</p>
                        <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
                          {getHorseName(expense.horse_id)}
                        </span>
                      </div>
                      {expense.vendor && (
                        <p className="text-sm text-muted-foreground truncate">{expense.vendor}</p>
                      )}
                      {expense.notes && (
                        <p className="text-xs text-muted-foreground truncate mt-1">{expense.notes}</p>
                      )}
                    </div>

                    <div className="text-right">
                      <p className="text-lg font-bold text-saddle">${expense.amount?.toFixed(2)}</p>
                      {expense.date && (
                        <p className="text-xs text-muted-foreground">
                          {format(parseISO(expense.date), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => { setEditingExpense(expense); setEditDialogOpen(true); }}
                        data-testid={`edit-expense-${expense.expense_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            data-testid={`delete-expense-${expense.expense_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this ${expense.amount?.toFixed(2)} expense? This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteExpense(expense.expense_id)}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingExpense(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Edit Expense</DialogTitle>
            </DialogHeader>
            {editingExpense && (
              <ExpenseForm
                data={editingExpense}
                onChange={setEditingExpense}
                onSubmit={handleEditExpense}
                horses={horses.filter(h => h.status === 'current' || h.horse_tab !== 'former')}
                isEdit={true}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
